import {useRef} from "react";
import {Skeleton, Stack} from "@mantine/core";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import {useReportAccess} from "../AccessContext";
import {ResourceError} from "../ResourceError";
import {StatusMessage} from "../StatusMessage";
import {renderNode} from "./renderNodeLegacy";
import type {DibDocument} from "../types";
import {TableOfContentsLEGACY} from "./TableOfContentsLEGACY";
import {RelatedContentLEGACY} from "./RelatedContentLEGACY";
import {SupplierInfoLEGACY} from "./SupplierInfoLEGACY";
import {useContentTitle} from "../useContentTitle";
import {useTocScrollSync} from "./useTocScrollSync";
import layoutClasses from "./ContentLayoutLEGACY.module.css";
import articleClasses from "./HtmlArticleLEGACY.module.css";

const DocumentSkeleton = () => (
    <Stack gap="sm" pt="xl">
        <Skeleton height={32} width="60%" mb="md" />
        <Skeleton height={14} width="100%" />
        <Skeleton height={14} width="95%" />
        <Skeleton height={14} width="88%" />
        <Skeleton height={14} width="92%" />
        <Skeleton height={14} width="40%" />
    </Stack>
);

const renderDescription = (doc: DibDocument) =>
    doc.descroot.flatMap((entry) => {
        const el = doc.elements[entry.id];
        if (!el?.children) return [];
        return el.children
            .filter((c): c is {id: string} => "id" in c)
            .map((c) => renderNode(doc, c.id, {}, c.id));
    });

const renderContent = (doc: DibDocument) =>
    doc.root.map((entry) => renderNode(doc, entry.id, {}, entry.id));

export const HTMLResourceLEGACY = ({
    resourceId,
    segmentId,
}: {
    resourceId: string;
    segmentId: string | undefined;
}) => {
    const {
        currentData: doc,
        isFetching,
        isError,
        error,
    } = dibAppApi.endpoints.getDocument.useQuery({resourceId, segmentId});

    useReportAccess(resourceId, doc?.access);

    const {articleRef, tocNavRef, activeId, handleTocClick} = useTocScrollSync(
        doc,
        resourceId,
        segmentId
    );

    // --- Segment continuity (issue #169) ---
    // currentData (doc) goes undefined the instant the segmentId query arg
    // changes to an uncached key, which is what blanks the TOC and info column
    // today. Retain the last loaded document and keep rendering the chrome from
    // it while a SAME-topic segment fetch is in flight.
    const prevDocRef = useRef<DibDocument | null>(null);
    if (doc) prevDocRef.current = doc;

    // Title from the retained doc so same-topic segment fetches (which blank
    // currentData) don't flicker the title back to "KAI".
    useContentTitle((doc ?? prevDocRef.current)?.name);

    // True only while fetching a new segment of the same topic. topicId is
    // stable across a top-level TOC click (the <Link> only changes segmentId),
    // so a different-topic navigation, a cold load, or an errored fetch all
    // leave this false and fall through to the full skeleton.
    const isIntraTopicFetch =
        !doc && isFetching && prevDocRef.current?.topicId === resourceId;

    // Drives the persistent chrome (TOC + info column). Live doc when present,
    // else the retained doc during an intra-topic fetch. The article keeps using
    // `doc` directly so it still shows the skeleton while the segment loads.
    const chromeDoc = doc ?? (isIntraTopicFetch ? prevDocRef.current : null);

    return (
        <div className={layoutClasses.page}>
            <nav ref={tocNavRef} className={layoutClasses.toc}>
                {chromeDoc && (
                    <TableOfContentsLEGACY
                        document={chromeDoc}
                        activeId={activeId}
                        onTocClick={handleTocClick}
                    />
                )}
            </nav>

            <article ref={articleRef} className={layoutClasses.article}>
                {isFetching && !doc && <DocumentSkeleton />}
                {isError && <ResourceError error={error} />}
                {doc && doc.root.length === 0 && (
                    <StatusMessage
                        title="Empty document"
                        detail="This document has no content to display."
                    />
                )}
                {doc && doc.root.length > 0 && (
                    <>
                        <h1 className={articleClasses.title}>{doc.name}</h1>
                        <div className={articleClasses.description}>
                            {renderDescription(doc)}
                        </div>
                        <div className={articleClasses.content}>
                            {renderContent(doc)}
                        </div>
                    </>
                )}
            </article>

            <aside className={layoutClasses.related}>
                {chromeDoc && (
                    <>
                        <SupplierInfoLEGACY document={chromeDoc} />
                        <RelatedContentLEGACY document={chromeDoc} />
                    </>
                )}
            </aside>
        </div>
    );
};
